import { type FormEvent, useState } from "react"
import { toast } from "sonner"
import { api } from "@/lib/api"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Download, Mail } from "lucide-react"
import { VpnPasswordDialog } from "@/components/VpnPasswordDialog"
import { rememberVpnPassword } from "@/lib/vpn-password-cache"

type DeliveryMethod = "download" | "email"

type Props = {
  open: boolean
  selectedMachine: string
  onClose: () => void
  onCreated: () => void | Promise<void>
}

export function VpnUserFormSheet({
  open,
  selectedMachine,
  onClose,
  onCreated,
}: Props) {
  const [fullName, setFullName] = useState("")
  const [email, setEmail] = useState("")
  const [username, setUsername] = useState("")
  const [autoPassword, setAutoPassword] = useState(true)
  const [password, setPassword] = useState("")
  const [passwordConfirm, setPasswordConfirm] = useState("")
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod | "">("")
  const [downloadPassword, setDownloadPassword] = useState<{
    username: string
    password: string
  } | null>(null)
  const [loading, setLoading] = useState(false)

  const reset = () => {
    setFullName("")
    setEmail("")
    setUsername("")
    setAutoPassword(true)
    setPassword("")
    setPasswordConfirm("")
    setDeliveryMethod("")
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!autoPassword && password !== passwordConfirm) {
      toast.error("A senha e a confirmação não coincidem.")
      return
    }
    if (!deliveryMethod) {
      toast.error("Escolha se deseja baixar o arquivo ou enviar por e-mail.")
      return
    }
    setLoading(true)
    try {
      const nextUsername = username.trim()
      const manualPassword = autoPassword ? "" : password
      const res = await api.createVpnUser({
        machineName: selectedMachine,
        clientName: nextUsername,
        fullName,
        email,
        password: autoPassword ? undefined : manualPassword,
        deliveryMethod,
      })
      toast.success("Usuário VPN criado.")
      if (deliveryMethod === "download") {
        try {
          await api.downloadVpnFile(selectedMachine, nextUsername)
          toast.success(`Arquivo ${nextUsername}.ovpn baixado.`)
        } catch (downloadErr) {
          toast.error(
            downloadErr instanceof Error
              ? downloadErr.message
              : "Erro ao baixar arquivo."
          )
        }
        const passwordToShow = res.generatedPassword ?? manualPassword
        if (passwordToShow) {
          rememberVpnPassword(selectedMachine, nextUsername, passwordToShow)
          setDownloadPassword({
            username: nextUsername,
            password: passwordToShow,
          })
        }
      } else {
        if (manualPassword) {
          rememberVpnPassword(selectedMachine, nextUsername, manualPassword)
        }
        if (res.mailLog) toast.info(res.mailLog)
      }
      await onCreated()
      handleClose()
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Erro ao criar usuário VPN."
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => !o && handleClose()}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Novo usuário VPN</SheetTitle>
          <SheetDescription>
            Cria um certificado VPN para o usuário no ambiente{" "}
            <span className="font-mono font-medium">{selectedMachine}</span>.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
          <div className="space-y-2">
            <Label htmlFor="vpn-fullname">Nome completo *</Label>
            <Input
              id="vpn-fullname"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              disabled={loading}
              autoComplete="off"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="vpn-email">E-mail *</Label>
            <Input
              id="vpn-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              autoComplete="off"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="vpn-username">Nome de usuário *</Label>
            <Input
              id="vpn-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              autoComplete="off"
              className="font-mono text-xs"
              placeholder="somente letras, números, _ ou -"
            />
          </div>

          <Separator />

          <div>
            <p className="mb-3 text-sm font-medium">Segurança</p>
            <p className="mb-3 text-xs text-muted-foreground">
              Mín. 8 caracteres: maiúscula, minúscula, número e símbolo (! @ # $
              % * - _).
            </p>
            <div className="mb-3 grid grid-cols-2 gap-2 rounded-lg border bg-muted/20 p-1">
              <Button
                type="button"
                variant={autoPassword ? "default" : "ghost"}
                className="h-auto min-h-10 whitespace-normal px-3 py-2 text-xs"
                onClick={() => setAutoPassword(true)}
                disabled={loading}
              >
                Gerar automaticamente
              </Button>
              <Button
                type="button"
                variant={!autoPassword ? "default" : "ghost"}
                className="h-auto min-h-10 whitespace-normal px-3 py-2 text-xs"
                onClick={() => setAutoPassword(false)}
                disabled={loading}
              >
                Definir manualmente
              </Button>
            </div>
            {!autoPassword && (
              <div className="flex flex-col gap-3">
                <div className="space-y-2">
                  <Label htmlFor="vpn-pw">Senha *</Label>
                  <Input
                    id="vpn-pw"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    autoComplete="new-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vpn-pw-confirm">Confirmação *</Label>
                  <Input
                    id="vpn-pw-confirm"
                    type="password"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    disabled={loading}
                    autoComplete="new-password"
                  />
                </div>
              </div>
            )}
          </div>

          <Separator />

          <div>
            <p className="mb-3 text-sm font-medium">Entrega do acesso</p>
            <div className="grid grid-cols-2 gap-2 rounded-lg border bg-muted/20 p-1">
              <Button
                type="button"
                variant={deliveryMethod === "download" ? "default" : "ghost"}
                className="h-auto min-h-10 whitespace-normal px-3 py-2 text-xs"
                onClick={() => setDeliveryMethod("download")}
                disabled={loading}
              >
                <Download className="size-4" />
                Download
              </Button>
              <Button
                type="button"
                variant={deliveryMethod === "email" ? "default" : "ghost"}
                className="h-auto min-h-10 whitespace-normal px-3 py-2 text-xs"
                onClick={() => setDeliveryMethod("email")}
                disabled={loading}
              >
                <Mail className="size-4" />
                Enviar por e-mail
              </Button>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={handleClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={
                loading ||
                !fullName.trim() ||
                !email.trim() ||
                !username.trim() ||
                !deliveryMethod ||
                (!autoPassword && !password)
              }
            >
              {loading ? "Criando..." : "Criar usuário VPN"}
            </Button>
          </div>
        </form>
        </SheetContent>
      </Sheet>
      <VpnPasswordDialog
        open={downloadPassword !== null}
        username={downloadPassword?.username ?? ""}
        password={downloadPassword?.password ?? ""}
        onClose={() => setDownloadPassword(null)}
      />
    </>
  )
}
